import classNames from 'classnames';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Spinner } from '../Spinner';

import styles from './Button.module.css';

export type ButtonHierarchy =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'link-color'
  | 'link-gray'
  | 'destructive'
  /** The outline form of `destructive`: a white surface, an error border and error text. */
  | 'destructive-secondary';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  hierarchy?: ButtonHierarchy;
  size?: ButtonSize;
  /** Icon rendered before the label — any node (`<Icon>`, an SVG, …). */
  iconLeading?: ReactNode;
  /** Icon rendered after the label. Ignored when `iconOnly`. */
  iconTrailing?: ReactNode;
  /**
   * Square icon button: only `iconLeading` shows. Provide an accessible name via
   * `aria-label`, or a string `children` (used as the label automatically).
   */
  iconOnly?: boolean;
  loading?: boolean;
  /**
   * When set (and `loading`), shows the spinner next to this text (the "loading with
   * label" variant). Omit for the spinner-only variant, which keeps the button's width
   * and swaps the label for a centered spinner.
   */
  loadingText?: ReactNode;
  children?: ReactNode;
}

/**
 * Button primitive — hierarchy × size, optional leading/trailing icons, icon-only,
 * and a loading state. Variants are driven by component-local CSS vars.
 *
 * @interim Ported from the reference apps; replaceable by the UI Kit without API breakage.
 */
export function Button({
  hierarchy = 'primary',
  size = 'md',
  iconLeading,
  iconTrailing,
  iconOnly = false,
  loading = false,
  loadingText,
  children,
  className,
  disabled,
  type = 'button',
  'aria-label': ariaLabel,
  ...rest
}: ButtonProps) {
  const isLink = hierarchy === 'link-color' || hierarchy === 'link-gray';
  const showLoadingText = loading && loadingText != null;
  const loaderOnly = loading && !showLoadingText;
  const spinnerClass = styles.spinner ?? '';

  // Keep an accessible name when the label isn't in the a11y tree (icon-only, or hidden
  // behind the spinner in the loader-only variant).
  const resolvedLabel =
    ariaLabel ?? ((iconOnly || loaderOnly) && typeof children === 'string' ? children : undefined);

  return (
    <button
      type={type}
      className={classNames(
        styles.button,
        styles[hierarchy],
        styles[`size-${size}`],
        iconOnly && styles['icon-only'],
        isLink && styles.link,
        loading && styles.loading,
        className,
      )}
      disabled={Boolean(disabled) || loading}
      aria-busy={loading || undefined}
      aria-label={resolvedLabel}
      {...rest}
    >
      {showLoadingText ? (
        <>
          <span className={styles.icon}>
            <Spinner className={spinnerClass} />
          </span>
          <span className={styles.text}>{loadingText}</span>
        </>
      ) : (
        <>
          {iconLeading && (
            <span className={classNames(styles.icon, loaderOnly && styles.invisible)}>
              {iconLeading}
            </span>
          )}
          {!iconOnly && children ? (
            <span className={classNames(styles.text, loaderOnly && styles.invisible)}>
              {children}
            </span>
          ) : null}
          {!iconOnly && iconTrailing && (
            <span className={classNames(styles.icon, loaderOnly && styles.invisible)}>
              {iconTrailing}
            </span>
          )}
          {loaderOnly && (
            <span className={styles['spinner-overlay']}>
              <Spinner className={spinnerClass} />
            </span>
          )}
        </>
      )}
    </button>
  );
}
